/**
 * HotelDetails Component
 * 
 * A specialized React component for displaying hotel-specific accommodation details.
 * Provides a rich, structured view of hotel information including check-in/out times,
 * stay duration, hotel contact details, and booking information.
 * 
 * Features:
 * - Visual stay duration card with check-in/out times
 * - Hotel-specific information display (name, address, phone, website)
 * - Room details and booking information
 * - Cost display with payment status
 * - Responsive grid layout with icon-based labels
 * - URL detection and automatic link creation
 * 
 * Expected Props:
 * @prop {Object} accommodation - The hotel accommodation data
 * @prop {Object} accommodation.details - Detailed hotel information
 *   @prop {string} details.checkInDate - ISO date string for check-in
 *   @prop {string} details.checkOutDate - ISO date string for check-out
 *   @prop {string} details.name - Hotel name
 *   @prop {string} [details.address] - Hotel address
 *   @prop {string} [details.phone] - Contact phone number
 *   @prop {string} [details.url] - Hotel website URL
 *   @prop {string} [details.roomType] - Type of room booked
 *   @prop {string} [details.bookingReference] - Booking confirmation number
 *   @prop {string} [details.bookingAgent] - Platform or agent used for booking
 *   @prop {string} [details.bookingUrl] - Booking confirmation URL
 * @prop {Object} [accommodation.cost] - Cost information
 *   @prop {number} cost.amount - The cost amount
 *   @prop {string} cost.currency - Currency code (e.g., 'USD')
 *   @prop {boolean} cost.paid - Payment status
 */

import React from 'react';
import { useParams } from 'react-router-dom';
import { DetailSection } from '../common';
import { format, parseISO } from 'date-fns';
import {
  FaHotel,
  FaCalendarAlt,
  FaClock,
  FaMapMarkerAlt,
  FaPhone,
  FaGlobe,
  FaBed,
  FaTicketAlt,
  FaMoneyBillWave
} from 'react-icons/fa';

const HotelDetails = ({ accommodation, days, onUnlink }) => {
  const { tripId } = useParams();

  // Early return if accommodation is null or missing
  if (!accommodation) {
    return <div className="error-message">Accommodation not found</div>;
  }

  const { details, cost } = accommodation;

  // Early return if no details are available
  if (!details) {
    return <div className="error-message">No hotel details available</div>;
  }

  /**
     * Formats a date string into a human-readable format
     * 
     * @param {string} dateStr - ISO date string to format
     * @returns {Object} Formatted date and time
     * @returns {string} .date - Formatted date (e.g., "Jan 1, 2024")
     * @returns {string} .time - Formatted time (e.g., "2:30 PM")
     * 
     * Error Handling:
     * - Returns "Not specified" for null/undefined dates
     * - Preserves original string if parsing fails
     */
  const formatDateTime = (dateStr) => {
    if (!dateStr) return { date: 'Not specified', time: '' };

    try {
      const date = parseISO(dateStr);
      return {
        date: format(date, 'MMM d, yyyy'),
        time: format(date, 'h:mm a')
      };
    } catch (e) {
      return { date: dateStr, time: '' };
    }
  };

  /**
     * Formats currency values with proper localization
     * 
     * @param {number} amount - The amount to format
     * @param {string} [currency='USD'] - The currency code
     * @returns {string} Formatted currency string (e.g., "$100.00")
     * 
     * Features:
     * - Uses Intl.NumberFormat for locale-aware formatting
     * - Falls back to basic string concatenation if formatting fails
     * - Handles undefined/null amounts gracefully
     */
  const formatCurrency = (amount, currency = 'USD') => {
    if (amount === undefined || amount === null) return 'Not specified';

    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency
      }).format(amount);
    } catch (e) {
      return `${amount} ${currency}`;
    }
  };

  // Process check-in/out dates into formatted objects
  const checkIn = {
    datetime: formatDateTime(details.checkInDate)
  };

  const checkOut = {
    datetime: formatDateTime(details.checkOutDate)
  };

  /**
     * Calculates the duration of stay in nights
     * 
     * @returns {number|null} Number of nights or null if calculation not possible
     * 
     * Features:
     * - Handles missing check-in/out dates
     * - Accounts for partial days
     * - Provides error handling with console logging
     */
  const calculateNights = () => {
    if (!details.checkInDate || !details.checkOutDate) return null;

    try {
      const checkInDate = new Date(details.checkInDate);
      const checkOutDate = new Date(details.checkOutDate);
      const diffTime = Math.abs(checkOutDate - checkInDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    } catch (e) {
      console.error('Error calculating nights:', e);
      return null;
    }
  };

  const nights = calculateNights();

  return (
    <div className="hotel-details">
      {/* Visual Stay Duration Card
               Displays check-in/out times with a visual representation of stay duration */}
      <div className="stay-card">
        <div className="stay-info">
          {/* Check-in Details with Icon */}
          <div className="check-in-info">
            <h3><FaCalendarAlt /> Check-in</h3>
            <div className="date-time">{checkIn.datetime.date}</div>
            {checkIn.datetime.time && <div className="time">{checkIn.datetime.time}</div>}
          </div>

          {/* Visual Stay Duration Indicator
                       Shows number of nights with a connecting line */}
          <div className="stay-divider">
            {nights && (
              <div className="nights-duration">
                <FaBed /> {nights} {nights === 1 ? 'night' : 'nights'}
              </div>
            )}
            <div className="stay-line"></div>
          </div>

          {/* Check-out Details with Icon */}
          <div className="check-out-info">
            <h3><FaCalendarAlt /> Check-out</h3>
            <div className="date-time">{checkOut.datetime.date}</div>
            {checkOut.datetime.time && <div className="time">{checkOut.datetime.time}</div>}
          </div>
        </div>

        {/* Related Days Section and Documents removed from here (moved to parent layout) */}
      </div>

      {/* Hotel Information Section
               Displays core hotel details with icons */}
      <DetailSection title="Hotel Information">
        <div className="details-grid">
          {/* Hotel Name (Required Field) */}
          <div className="detail-item">
            <div className="detail-label">
              <FaHotel /> Hotel Name
            </div>
            <div className="detail-value">{details.name || 'Not specified'}</div>
          </div>

          {/* Optional Hotel Details
                       Each field is conditionally rendered if present */}
          {details.address && (
            <div className="detail-item">
              <div className="detail-label">
                <FaMapMarkerAlt /> Address
              </div>
              <div className="detail-value">{details.address}</div>
            </div>
          )}

          {details.phone && (
            <div className="detail-item">
              <div className="detail-label">
                <FaPhone /> Phone
              </div>
              <div className="detail-value">{details.phone}</div>
            </div>
          )}

          {details.url && (
            <div className="detail-item">
              <div className="detail-label">
                <FaGlobe /> Website
              </div>
              <div className="detail-value">
                <a href={details.url} target="_blank" rel="noopener noreferrer">
                  {details.url}
                </a>
              </div>
            </div>
          )}

          {details.roomType && (
            <div className="detail-item">
              <div className="detail-label">
                <FaBed /> Room Type
              </div>
              <div className="detail-value">{details.roomType}</div>
            </div>
          )}
        </div>
      </DetailSection>

      {/* Booking Information Section
               Displays reservation details and payment information */}
      <DetailSection title="Booking Information">
        <div className="details-grid">
          {/* Booking Reference Number */}
          {details.bookingReference && (
            <div className="detail-item">
              <div className="detail-label">
                <FaTicketAlt /> Booking Reference
              </div>
              <div className="detail-value">{details.bookingReference}</div>
            </div>
          )}

          {/* Booking Platform/Agent */}
          {details.bookingAgent && (
            <div className="detail-item">
              <div className="detail-label">Booking Agent</div>
              <div className="detail-value">{details.bookingAgent}</div>
            </div>
          )}

          {/* Booking Confirmation URL */}
          {details.bookingUrl && (
            <div className="detail-item">
              <div className="detail-label">Booking URL</div>
              <div className="detail-value">
                <a href={details.bookingUrl} target="_blank" rel="noopener noreferrer">
                  {details.bookingUrl}
                </a>
              </div>
            </div>
          )}

          {/* Cost Information with Payment Status */}
          {cost && cost.amount > 0 && (
            <div className="detail-item">
              <div className="detail-label">
                <FaMoneyBillWave /> Cost
              </div>
              <div className="detail-value">
                {formatCurrency(cost.amount, cost.currency)}
                <span className="payment-status">
                  {cost.paid ? ' (Paid)' : ' (Unpaid)'}
                </span>
              </div>
            </div>
          )}
        </div>
      </DetailSection>
    </div>
  );
};

export default HotelDetails; 